import Vue from 'vue'
import FormDate from '@/agilis/components/Form/FormDate/FormDate.vue'
import FormDatetime from '@/agilis/components/Form/FormDatetime/FormDatetime.vue'
import FormTime from '@/agilis/components/Form/FormTime/FormTime.vue'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'

// Descriptor for default rules by type: if a type is not listed,
// the field will be dropped from the list of available fields
const DEFAULT_RULES = {
  number: {
    operator: 'EQUALS',
    value: '0'
  },
  text: {
    operator: 'EQUALS',
    value: 'lorem ipsum'
  },
  date: {
    operator: 'EQUALS',
    value: '2021-10-10'
  },
  datetime: {
    operator: 'EQUALS',
    value: '2021-10-05T14:56:37Z'
  },
  time: {
    operator: 'EQUALS',
    value: '09:00:00'
  },
  tag: {
    operator: 'LIKE',
    value: 'lorem ipsum'
  }
}

const SUPPORTED_OPERATIONS = {
  date: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'IS_NULL', 'IS_NOT_NULL'],
  datetime: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'IS_NULL', 'IS_NOT_NULL'],
  number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'IN', 'NOT_IN', 'IS_NULL', 'IS_NOT_NULL'],
  text: ['EQUALS', 'NOT_EQUALS', 'LIKE', 'NOT_LIKE', 'IN', 'NOT_IN', 'IS_NULL', 'IS_NOT_NULL'],
  tag: ['LIKE', 'NOT_LIKE', 'IS_NULL', 'IS_NOT_NULL'],
  time: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'IS_NULL', 'IS_NOT_NULL']
}

export default {
  name: 'FilterTerm',
  mixins: [toursMixin],

  props: {
    headers: Array,
    availableHeaders: Array,
    filterTerms: {
      type: Array,
      default: () => []
    },
    sortingTerms: {
      type: Array,
      default: () => []
    },
    fields: Array,
    locked: Boolean
  },

  components: {
    FormDate,
    FormDatetime,
    FormTime
  },

  computed: {
    availableFields () {
      const a = this.fields.filter(f => DEFAULT_RULES[f.type]) // type supported
        .map(f => ({ value: f.id, text: f.name ? this.$t(f.name) : f.id }))
        .sort((a, b) => a.text.toLowerCase() > b.text.toLowerCase() ? 1 : -1)

      return a
    },
    editors () {
      const ee = this.filterTerms.map((r) => {
        const field = this.fields.find(f => f.id === r.field)

        const operations = SUPPORTED_OPERATIONS[field?.type] || []

        return {
          ...r,
          operations: operations.map(o => ({ value: o, text: this.$t(`label.filtersOperation${o}`) })),
          invalidField: (r.field && (!field || !operations.includes(r.operator))),
          invalidValue: r.invalid,
          type: field?.type
        }
      })
      return ee
    },

    availableSortFields () {
      const a = this.fields.filter(f => DEFAULT_RULES[f.type] && // type supported
        !this.sortingTerms.find(r => r.sortField === f.id) // no filter set yet on the field
      ).map(f => ({ value: f.id, text: f.name ? this.$t(f.name) : f.id }))
        .sort((a, b) => a.text.toLowerCase() > b.text.toLowerCase() ? 1 : -1)

      return a
    },

    sortEditors () {
      const ee = this.sortingTerms.map((r) => {
        const field = this.fields.find(f => f.id === r.sortField)

        return {
          ...r,
          invalid: !field,
          items: field ? [{
            value: field.id,
            text: this.$t(field.name)
          }].concat(this.availableSortFields) : []
        }
      })
      return ee
    },

    sortOperations () {
      return [
        { value: 'ASC', text: this.$t('label.filtersSortTypeASC') },
        { value: 'DESC', text: this.$t('label.filtersSortTypeDESC') }
      ]
    },

    availableViewHeaders () {
      return this.availableHeaders
        .map(header => ({ value: header.value, text: header.text }))
    },

    selectedViewHeaders () {
      return this.headers
        .map(header => ({ value: header.value, text: header.text }))
    }
  },

  created () {

  },

  methods: {
    addNewRule () {
      this.filterTerms.push(
        { field: null }
      )
      this.$emit('change')
    },

    onChooseRuleField (ruleIndex, fieldName) {
      const field = this.fields.find(f => f.id === fieldName)
      const rule = Object.assign({}, DEFAULT_RULES[field.type], {
        field: fieldName
      })
      Vue.set(this.filterTerms, ruleIndex, rule)
      this.$emit('change')
    },

    changeValue (filterTermIndex, nextValue) {
      if (this.filterTerms[filterTermIndex].value !== nextValue.trim()) {
        // Update the filterTerm (<select> bind is with a derived object!)
        this.filterTerms[filterTermIndex].value = nextValue.trim()

        this.$emit('change')
      }
    },

    changeValues (filterTermIndex, nextValues) {
      // Update the filterTerm (<select> bind is with a derived object!)
      this.filterTerms[filterTermIndex].values = typeof nextValues === 'string' ? nextValues.split(',').map(s => s.trim()) : nextValues

      this.$emit('change')
    },

    changeRuleField (filterTermIndex, nextField) {
      const prevFieldType = this.fields.find(f => f.id === this.filterTerms[filterTermIndex].field)?.type
      const nextFieldType = this.fields.find(f => f.id === nextField)?.type

      if (prevFieldType === nextFieldType) {
        this.filterTerms[filterTermIndex].field = nextField
      } else {
        const rule = Object.assign({}, DEFAULT_RULES[nextFieldType], {
          field: nextField
        })
        Vue.set(this.filterTerms, filterTermIndex, rule)
      }

      this.$emit('change')
    },

    changeOperation (filterTermIndex, nextOperator) {
      const ff = this.filterTerms[filterTermIndex]

      // Update the filterTerm (<select> bind is with a derived object!)
      this.filterTerms[filterTermIndex].operator = nextOperator

      // check operator/value consistency
      if (this.isArrayOperator(nextOperator) && ff.values === undefined) {
        Vue.set(this.filterTerms[filterTermIndex], 'values', ff.value ? [ff.value] : [])
        Vue.delete(this.filterTerms[filterTermIndex], 'value')
        delete this.filterTerms[filterTermIndex].value
      } else if (nextOperator === 'IS_NULL' || nextOperator === 'IS_NOT_NULL') {
        Vue.delete(this.filterTerms[filterTermIndex], 'value')
        Vue.delete(this.filterTerms[filterTermIndex], 'values')
        delete this.filterTerms[filterTermIndex].value
      } else if (!this.isArrayOperator(nextOperator) && ff.value === undefined) {
        Vue.set(this.filterTerms[filterTermIndex], 'value', ff.values?.[0] || '')
        Vue.delete(this.filterTerms[filterTermIndex], 'values')
      }

      this.$emit('change')
    },

    deleteTerm (filterTermIndex) {
      if (filterTermIndex > -1) {
        this.filterTerms.splice(filterTermIndex, 1)
        this.$emit('change')
      }
    },

    addNewSort (fieldName) {
      this.sortingTerms.push({ sortField: null })
      this.$emit('change')
    },

    onChooseSortField (sortIndex, fieldName) {
      const sort = { sortField: fieldName, sortType: 'ASC' }
      Vue.set(this.sortingTerms, sortIndex, sort)
      this.$emit('change')
    },

    onChangeSortField (sortIndex, fieldName) {
      this.sortingTerms[sortIndex].sortField = fieldName
      this.$emit('change')
    },

    deleteSortTerm  (ff) {
      const index = this.sortingTerms.findIndex(f => f.sortField === ff.sortField)
      if (index > -1) {
        this.sortingTerms.splice(index, 1)
        this.$emit('change')
      }
    },

    onChooseOrder (sortIndex, order) {
      this.sortingTerms[sortIndex].sortType = order
      this.$emit('change')
    },

    changeHeaders (val) {
      this.headers.splice(0)
      this.headers.push(...val)
      this.$emit('change')
    },

    isArrayOperator (operator) {
      return ['IN', 'NOT_IN'].includes(operator)
    }
  }
}
